import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AgentDefinition,
  AgentDefinitionQuery,
  AgentDefinitionsQueryBuilder,
  BulkUpdateAgentDefinitionTagsApplicationErrors,
  BulkUpdateAgentDefinitionTagsByFilterApplicationErrors,
  BulkUpdateAgentDefinitionTagsByFilterOptions,
  BulkUpdateAgentDefinitionTagsByFilterResponse,
  BulkUpdateAgentDefinitionTagsOptions,
  BulkUpdateAgentDefinitionTagsResponse,
  QueryAgentDefinitionsResponse,
  UpdateAgentDefinition,
  bulkUpdateAgentDefinitionTags as universalBulkUpdateAgentDefinitionTags,
  bulkUpdateAgentDefinitionTagsByFilter as universalBulkUpdateAgentDefinitionTagsByFilter,
  createAgentDefinition as universalCreateAgentDefinition,
  deleteAgentDefinition as universalDeleteAgentDefinition,
  getAgentDefinition as universalGetAgentDefinition,
  queryAgentDefinitions as universalQueryAgentDefinitions,
  typedQueryAgentDefinitions as universalTypedQueryAgentDefinitions,
  updateAgentDefinition as universalUpdateAgentDefinition,
} from './agents-v1-agent-definition-agent-registry.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/agents' };

/** @internal */
export function createAgentDefinition(
  httpClient: HttpClient
): CreateAgentDefinitionSignature {
  return (
    agentDefinition: NonNullablePaths<
      AgentDefinition,
      `displayName` | `key` | `provider` | `runtime`,
      0
    >
  ) =>
    universalCreateAgentDefinition(
      agentDefinition,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateAgentDefinitionSignature {
  /**
   * Creates an AgentDefinition.
   * @param - AgentDefinition to be created.
   * @returns The created AgentDefinition.
   */
  (
    agentDefinition: NonNullablePaths<
      AgentDefinition,
      `displayName` | `key` | `provider` | `runtime`,
      0
    >
  ): Promise<
    NonNullablePaths<
      AgentDefinition,
      | `key`
      | `displayName`
      | `provider`
      | `runtime`
      | `status`
      | `capabilities`
      | `tags.privateTags.tagIds`,
      2
    >
  >;
}

/** @internal */
export function getAgentDefinition(
  httpClient: HttpClient
): GetAgentDefinitionSignature {
  return (agentDefinitionId: string) =>
    universalGetAgentDefinition(
      agentDefinitionId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetAgentDefinitionSignature {
  /**
   * Retrieves an AgentDefinition.
   * @param - ID of the AgentDefinition to retrieve.
   * @returns The requested AgentDefinition.
   */
  (agentDefinitionId: string): Promise<
    NonNullablePaths<
      AgentDefinition,
      | `key`
      | `displayName`
      | `provider`
      | `runtime`
      | `status`
      | `capabilities`
      | `tags.privateTags.tagIds`,
      2
    >
  >;
}

/** @internal */
export function updateAgentDefinition(
  httpClient: HttpClient
): UpdateAgentDefinitionSignature {
  return (
    _id: string,
    agentDefinition: NonNullablePaths<UpdateAgentDefinition, `revision`, 0>
  ) =>
    universalUpdateAgentDefinition(
      _id,
      agentDefinition,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateAgentDefinitionSignature {
  /**
   * Updates an AgentDefinition.
   * @param - AgentDefinition ID.
   * @returns Updated AgentDefinition.
   */
  (
    _id: string,
    agentDefinition: NonNullablePaths<UpdateAgentDefinition, `revision`, 0>
  ): Promise<
    NonNullablePaths<
      AgentDefinition,
      | `key`
      | `displayName`
      | `provider`
      | `runtime`
      | `status`
      | `capabilities`
      | `tags.privateTags.tagIds`,
      2
    >
  >;
}

/** @internal */
export function deleteAgentDefinition(
  httpClient: HttpClient
): DeleteAgentDefinitionSignature {
  return (agentDefinitionId: string) =>
    universalDeleteAgentDefinition(
      agentDefinitionId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteAgentDefinitionSignature {
  /**
   * Deletes an AgentDefinition.
   * @param - ID of the AgentDefinition to delete.
   */
  (agentDefinitionId: string): Promise<void>;
}

/** @internal */
export function queryAgentDefinitions(
  httpClient: HttpClient
): QueryAgentDefinitionsSignature {
  return () =>
    universalQueryAgentDefinitions(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryAgentDefinitionsSignature {
  /**
   * Retrieves a list of AgentDefinitions by WQL query.
   */
  (): AgentDefinitionsQueryBuilder;
}

export function typedQueryAgentDefinitions(
  httpClient: HttpClient
): TypedQueryAgentDefinitionsSignature {
  return (query: AgentDefinitionQuery) =>
    universalTypedQueryAgentDefinitions(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryAgentDefinitionsSignature {
  /** */
  (query: AgentDefinitionQuery): Promise<
    NonNullablePaths<
      QueryAgentDefinitionsResponse,
      | `agentDefinitions`
      | `agentDefinitions.${number}.key`
      | `agentDefinitions.${number}.displayName`
      | `agentDefinitions.${number}.provider`
      | `agentDefinitions.${number}.runtime`
      | `agentDefinitions.${number}.status`,
      2
    >
  >;
}

/** @internal */
export function bulkUpdateAgentDefinitionTags(
  httpClient: HttpClient
): BulkUpdateAgentDefinitionTagsSignature {
  return (
    agentDefinitionIds: string[],
    options?: BulkUpdateAgentDefinitionTagsOptions
  ) =>
    universalBulkUpdateAgentDefinitionTags(
      agentDefinitionIds,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateAgentDefinitionTagsSignature {
  /**
   * Synchronously updates tags on multiple AgentDefinitions by ID.
   * @param - IDs of AgentDefinitions whose tags will be updated.
   */
  (
    agentDefinitionIds: string[],
    options?: BulkUpdateAgentDefinitionTagsOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateAgentDefinitionTagsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      4
    > & {
      __applicationErrorsType?: BulkUpdateAgentDefinitionTagsApplicationErrors;
    }
  >;
}

/** @internal */
export function bulkUpdateAgentDefinitionTagsByFilter(
  httpClient: HttpClient
): BulkUpdateAgentDefinitionTagsByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: BulkUpdateAgentDefinitionTagsByFilterOptions
  ) =>
    universalBulkUpdateAgentDefinitionTagsByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateAgentDefinitionTagsByFilterSignature {
  /**
   * Asynchronously updates tags on AgentDefinitions matching the provided filter.
   * @param - Filter that selects AgentDefinitions whose tags will be updated.
   */
  (
    filter: Record<string, any>,
    options?: BulkUpdateAgentDefinitionTagsByFilterOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateAgentDefinitionTagsByFilterResponse,
      `jobId`,
      0
    > & {
      __applicationErrorsType?: BulkUpdateAgentDefinitionTagsByFilterApplicationErrors;
    }
  >;
}

export {
  AccountInfo,
  ActionEvent,
  AgentDefinition,
  AgentDefinitionQuerySpec,
  AgentDefinitionTagsModified,
  AgentDefinitionsQueryBuilder,
  AgentDefinitionsQueryResult,
  AgentStatus,
  ApplicationError,
  BulkActionMetadata,
  BulkUpdateAgentDefinitionTagsByFilterOptions,
  BulkUpdateAgentDefinitionTagsByFilterRequest,
  BulkUpdateAgentDefinitionTagsByFilterResponse,
  BulkUpdateAgentDefinitionTagsOptions,
  BulkUpdateAgentDefinitionTagsRequest,
  BulkUpdateAgentDefinitionTagsResponse,
  BulkUpdateAgentDefinitionTagsResult,
  CreateAgentDefinitionRequest,
  CreateAgentDefinitionResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DeleteAgentDefinitionRequest,
  DeleteAgentDefinitionResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  ExtendedFields,
  GetAgentDefinitionRequest,
  GetAgentDefinitionResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemMetadata,
  MessageEnvelope,
  QueryAgentDefinitionsRequest,
  QueryAgentDefinitionsResponse,
  RestoreInfo,
  SortOrder,
  Sorting,
  TagList,
  Tags,
  UpdateAgentDefinition,
  UpdateAgentDefinitionRequest,
  UpdateAgentDefinitionResponse,
  WebhookIdentityType,
  utils,
} from './agents-v1-agent-definition-agent-registry.universal.js';
